import { useCallback, useEffect, useRef, useState } from 'react';
import { DateRangePicker } from 'react-date-range';
import { endOfDay } from 'date-fns/endOfDay';
import { format } from 'date-fns/format';
import { isSameDay } from 'date-fns/isSameDay';
import { isValid } from 'date-fns/isValid';
import { startOfDay } from 'date-fns/startOfDay';
import { formatInTimeZone } from 'date-fns-tz';
import { ActionDescription } from './Onboarding';
import {
  formatMatchRangeForDisplay,
  storeEndOfCalendarDay,
  storeMatchRangeToday,
  storeMatchRangeYesterday,
  storeStartOfCalendarDay,
} from '../utils/storeDateRange';

const RANGE_KEY = 'selection';
const ACCENT = '#F8A305';

function safeDate(d) {
  if (d instanceof Date && isValid(d)) return d;
  return new Date();
}

/**
 * react-date-range compares days in the browser's local timezone. Map store wall dates to
 * local Date objects with the same calendar y-m-d so the highlight matches the label.
 */
function storeWallToBrowserLocalDate(utcOrDate, storeTimezone) {
  const ymd = formatInTimeZone(safeDate(utcOrDate), storeTimezone, 'yyyy-MM-dd');
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function toSelection(match, storeTimezone) {
  if (!match?.start || !match?.end) {
    const now = new Date();
    return { startDate: now, endDate: now, key: RANGE_KEY, color: ACCENT };
  }
  if (storeTimezone) {
    return {
      startDate: storeWallToBrowserLocalDate(match.start, storeTimezone),
      endDate: storeWallToBrowserLocalDate(match.end, storeTimezone),
      key: RANGE_KEY,
      color: ACCENT,
    };
  }
  return {
    startDate: safeDate(match.start),
    endDate: safeDate(match.end),
    key: RANGE_KEY,
    color: ACCENT,
  };
}

/**
 * True when calendar selection matches the applied range.
 * Uses store-timezone wall dates for both `match` and picker instants (do not use local `startOfDay`
 * on picker dates — that breaks when the browser TZ differs from the store).
 */
function pendingRangeEqualsMatch(timezone, match, selStart, selEnd) {
  if (!selStart || !selEnd || !isValid(selStart) || !isValid(selEnd)) return true;
  if (!match?.start || !match?.end) return false;
  if (timezone) {
    const mStart = formatInTimeZone(safeDate(match.start), timezone, 'yyyy-MM-dd');
    const mEnd = formatInTimeZone(safeDate(match.end), timezone, 'yyyy-MM-dd');
    const pStart = format(selStart, 'yyyy-MM-dd');
    const pEnd = format(selEnd, 'yyyy-MM-dd');
    return mStart === pStart && mEnd === pEnd;
  }
  const a = startOfDay(selStart).getTime();
  const b = startOfDay(selEnd).getTime();
  return (
    startOfDay(safeDate(match.start)).getTime() === a &&
    startOfDay(safeDate(match.end)).getTime() === b
  );
}

/**
 * Store-aware date-only range: one summary control + popover (presets + react-date-range).
 * Presets call `options[].action()`; custom selection calls the Custom Range option’s action(start, end)
 * with calendar start-of-day / end-of-day Dates (parents keep existing timezone → API mapping).
 */
export default function StoreDateRangeFilter({
  options,
  active,
  setActive,
  match,
  disabled,
  timezone,
  className,
  inputId,
  highlight,
  tutorialStart,
  tutorial,
  setTutorial,
  finish,
  future,
  /** 'end' (default): anchor popover to trigger's right edge (opens left). 'start': anchor left (opens right). */
  popoverAlign = 'end',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const prevOpenRef = useRef(false);
  const [ranges, setRanges] = useState(() => [toSelection(match, timezone)]);

  useEffect(() => {
    setRanges([toSelection(match, timezone)]);
  }, [match?.start, match?.end, timezone]);

  /** Closing the popover without Apply discards in-progress calendar selection. */
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      setRanges([toSelection(match, timezone)]);
    }
    prevOpenRef.current = open;
  }, [open, match?.start, match?.end, timezone]);

  const customOpt = options.find((o) => o.text === 'Custom Range');

  const applyCustom = useCallback(
    (startDate, endDate) => {
      let start;
      let end;
      if (timezone) {
        const startYmd = format(startDate, 'yyyy-MM-dd');
        const endYmd = format(endDate, 'yyyy-MM-dd');
        start = storeStartOfCalendarDay(timezone, startYmd);
        end = storeEndOfCalendarDay(timezone, endYmd);
      } else {
        start = startOfDay(startDate);
        end = endOfDay(endDate);
      }
      if (typeof customOpt?.action === 'function') {
        customOpt.action(start, end);
      }
      setActive('Custom Range');
    },
    [customOpt, setActive, timezone]
  );

  const applyWallPreset = useCallback(
    (label, startDate, endDate) => {
      const start = startOfDay(startDate);
      const end = endOfDay(endDate);
      if (typeof customOpt?.action === 'function') {
        customOpt.action(start, end);
      }
      setActive(label);
      setOpen(false);
    },
    [customOpt, setActive]
  );

  const onRangeChange = useCallback(
    (next) => {
      const sel = next[RANGE_KEY];
      if (!sel?.startDate || !sel?.endDate) return;
      setRanges([{ ...sel, key: RANGE_KEY, color: ACCENT }]);
      if (!pendingRangeEqualsMatch(timezone, match, sel.startDate, sel.endDate)) {
        setActive('Custom Range');
      }
    },
    [setActive, timezone, match]
  );

  const applyCalendarRange = useCallback(() => {
    const sel = ranges[0];
    if (!sel?.startDate || !sel?.endDate || !isValid(sel.startDate) || !isValid(sel.endDate)) return;
    applyCustom(sel.startDate, sel.endDate);
    setOpen(false);
  }, [ranges, applyCustom]);

  const canApplyCalendar =
    ranges[0]?.startDate &&
    ranges[0]?.endDate &&
    isValid(ranges[0].startDate) &&
    isValid(ranges[0].endDate);

  const showApplyButton =
    canApplyCalendar &&
    !pendingRangeEqualsMatch(timezone, match, ranges[0].startDate, ranges[0].endDate);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const presetOptions = options.filter((o) => o.text !== 'Custom Range');
  /** Last selectable calendar day in local grid = store's current date (react-date-range uses local isAfter vs maxDate). */
  const calendarMaxDate =
    future === true ? undefined : timezone ? storeWallToBrowserLocalDate(new Date(), timezone) : new Date();

  const renderStoreDayContent = useCallback(
    (day) => {
      const storeTodayLocal = storeWallToBrowserLocalDate(new Date(), timezone);
      const isStoreToday = isSameDay(day, storeTodayLocal);
      return (
        <span className={isStoreToday ? 'store-date-range-popover__store-today-day' : undefined}>
          {format(day, 'd')}
        </span>
      );
    },
    [timezone]
  );

  const menuHighlight = tutorial > 0 && tutorial === tutorialStart + 1;

  return (
    <div
      ref={rootRef}
      className={`store-date-range-filter${className ? ` ${className}` : ''}`}
      style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}
    >
      {tutorial > 0 && tutorial === tutorialStart && (
        <ActionDescription
          text="Open this control, then pick a preset or drag a range on the calendar."
          style={{ width: '200px', bottom: '', left: '-220px' }}
          triangle={{ right: '-18px', top: 'calc(50% - 9px)', transform: 'rotate(90deg)' }}
          tutorial={tutorial}
          setTutorial={setTutorial}
          next="Next"
          hidePopup={true}
        />
      )}
      {tutorial > 0 && tutorial === tutorialStart + 1 && (
        <ActionDescription
          text="Presets are on the left; pick dates on the calendar, then click Apply to confirm."
          style={{ width: '210px', bottom: '', left: '8px' }}
          triangle={{ right: '-18px', top: 'calc(50% - 9px)', transform: 'rotate(90deg)' }}
          tutorial={tutorial}
          setTutorial={setTutorial}
          finish={finish}
          hidePopup={true}
        />
      )}
      <button
        type="button"
        id={inputId}
        className={`store-date-range-filter__trigger date-filter__input${disabled ? ' date-filter__input--disabled' : ''}`}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
        style={
          highlight || menuHighlight
            ? {
                position: 'relative',
                zIndex: 55,
                backgroundColor: '#fff',
                boxShadow: '0 0 10px #fff',
              }
            : {}
        }
      >
        <span className="date-filter__input-text">{formatMatchRangeForDisplay(timezone, active, match)}</span>
        <i className="fa-solid fa-calendar-days store-date-range-filter__cal" aria-hidden />
      </button>
      {open && !disabled && (
        <div
          className={`store-date-range-popover${timezone ? ' store-date-range-popover--store-tz' : ''}${menuHighlight ? ' store-date-range-popover--tutorial' : ''}${popoverAlign === 'start' ? ' store-date-range-popover--align-start' : ''}`}
          style={
            menuHighlight
              ? { zIndex: 2000, backgroundColor: '#fff', boxShadow: '0 0 10px #fff' }
              : undefined
          }
        >
          <div className="store-date-range-popover__presets">
            <button
              type="button"
              className={`store-date-range-popover__preset${active === 'Today' ? ' store-date-range-popover__preset--active' : ''}`}
              onClick={() => {
                if (timezone) {
                  const { start, end } = storeMatchRangeToday(timezone);
                  if (typeof customOpt?.action === 'function') {
                    customOpt.action(start, end);
                  }
                  setActive('Today');
                  setOpen(false);
                } else {
                  applyWallPreset('Today', new Date(), new Date());
                }
              }}
            >
              Today
            </button>
            <button
              type="button"
              className={`store-date-range-popover__preset${active === 'Yesterday' ? ' store-date-range-popover__preset--active' : ''}`}
              onClick={() => {
                if (timezone) {
                  const { start, end } = storeMatchRangeYesterday(timezone);
                  if (typeof customOpt?.action === 'function') {
                    customOpt.action(start, end);
                  }
                  setActive('Yesterday');
                  setOpen(false);
                } else {
                  const y = new Date();
                  y.setDate(y.getDate() - 1);
                  applyWallPreset('Yesterday', y, y);
                }
              }}
            >
              Yesterday
            </button>
            {presetOptions.map((opt) => (
              <button
                type="button"
                key={opt.text}
                className={`store-date-range-popover__preset${active === opt.text ? ' store-date-range-popover__preset--active' : ''}`}
                onClick={() => {
                  opt.action();
                  setActive(opt.text);
                  setOpen(false);
                }}
              >
                {opt.text}
              </button>
            ))}
            <button
              type="button"
              className={`store-date-range-popover__preset${active === 'Custom Range' ? ' store-date-range-popover__preset--active' : ''}`}
              onClick={() => {
                setActive('Custom Range');
              }}
            >
              Custom Range
            </button>
          </div>
          <div className="store-date-range-popover__calendar">
            <DateRangePicker
              ranges={ranges}
              onChange={onRangeChange}
              moveRangeOnFirstSelection={false}
              months={2}
              direction="horizontal"
              weekStartsOn={1}
              maxDate={calendarMaxDate}
              dayContentRenderer={timezone ? renderStoreDayContent : undefined}
              showMonthAndYearPickers
              rangeColors={[ACCENT]}
              staticRanges={[]}
              inputRanges={[]}
            />
            {showApplyButton && (
              <div className="store-date-range-popover__calendar-footer">
                <button type="button" className="store-date-range-popover__apply" onClick={applyCalendarRange}>
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
